import React, { useCallback, useContext, useEffect, useState } from "react"
import {
    View,
    ScrollView,
    StyleSheet,
    ActivityIndicator,
    Text,
} from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import SummerTutorsContent from "./components/SummerTutorsContent"
import SummerHelpBanner from "./components/SummerHelpBanner"

/**
 * Étape "aha moment tuteur humain" — entre le programme (summer/plan) et le
 * checkout 1 € (summer/slots-checkout). Montre qu'il y a de vrais humains
 * derrière l'offre : un compte de masteurs qui correspondent au profil de
 * l'enfant (matières + niveau, calculé backend), 2 cartes vitrine + "et N
 * autres", et un bloc de réassurance sur la procédure d'attribution.
 *
 * Étape NON bloquante : le compte + les cartes viennent de
 * POST /summer-funnel/{stateId}/matching-tutors, mais en cas d'erreur, de
 * réponse vide ou de contexte pas encore hydraté, on affiche un repli gracieux
 * (titre rassurant sans chiffre + bloc garanties) et le CTA "Continuer" route
 * TOUJOURS vers le checkout. Jamais de spinner infini, jamais de cul-de-sac.
 *
 * Même largeur de colonne (480) et même langage visuel que SummerPlanScreen.
 */

const CONTENT_MAX = 480

const SummerTutorsScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state } = useSummerFunnel()
    // Refresh-safe / URL-partageable (voir useSummerFunnelGuard).
    const { nextParams } = useSummerFunnelGuard()

    const [loading, setLoading] = useState(true)
    const [matching, setMatching] = useState(null)
    const [failed, setFailed] = useState(false)

    const containerWidth = Math.min(dimensions?.width || 400, CONTENT_MAX)
    const firstname = state.studentFirstname || "votre enfant"

    // Id d'état valide requis pour l'appel matching. Sur contexte froid (refresh /
    // lien magique pas encore hydraté), l'id peut être null/undefined → la garde
    // route vers `summer/resume` ; on attend l'hydratation sans tirer l'appel.
    const funnelStateId = Number(state.funnelStateId)
    const hasValidStateId = Number.isInteger(funnelStateId) && funnelStateId > 0
    // Le matching lit le panier dans le body (comme `program`) : matières + classe.
    // Sans eux l'appel partirait en 422 → on saute directement au repli gracieux
    // (jamais bloquant), le CTA continue vers le checkout.
    const subjects = state.subjects || []
    const hasMatchingPayload =
        hasValidStateId && subjects.length > 0 && !!state.studentClass

    useEffect(() => {
        amplitude.track("summer_funnel_tutors_viewed", {
            funnel_state_id: state.funnelStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Charge le matching au focus (comme SummerPlanScreen charge le programme).
    // Une seule passe par hydratation : tant qu'on n'a pas un id valide on garde
    // le spinner (la reprise est en cours) ; dès qu'il est là on tente une fois.
    useFocusEffect(
        useCallback(() => {
            // Contexte froid : la garde s'occupe de rediriger vers summer/resume.
            // On laisse le spinner refléter la reprise sans rien tirer.
            if (!hasValidStateId) return
            // Déjà chargé (succès ou repli) : pas de re-fetch. Le matching + le tirage
            // ne dépendent que du panier (matières + classe + créneaux/mois), figés à
            // ce stade du funnel — un seul appel par hydratation suffit.
            if (matching || failed) return
            // Panier incomplet (cas limite, ex. accès direct) → repli direct, pas
            // d'appel voué au 422. L'étape reste NON bloquante.
            if (!hasMatchingPayload) {
                setFailed(true)
                setLoading(false)
                return
            }

            let cancelled = false
            setLoading(true)
            setFailed(false)
            summerFunnelService
                .getMatchingTutors(
                    { stateId: funnelStateId },
                    {
                        subjects,
                        studentClass: state.studentClass,
                        // Créneaux + mois choisis : la couverture les ignore, mais ils
                        // enrichissent la signature de tirage déterministe côté backend
                        // (mêmes params → mêmes 2 cartes, params différents → tirage
                        // différent). Omis si l'étape configurateur n'a pas (encore)
                        // figé les créneaux — la signature retombe sur matières + classe.
                        slotIds: state.selectedSlotIds || [],
                        months: state.selectedMonths || [],
                    }
                )
                .then((res) => {
                    if (cancelled) return
                    const payload = res?.data?.data ?? res?.data ?? null
                    // Réponse vide ou sans compte exploitable → repli gracieux.
                    if (!payload || !(payload.count > 0)) {
                        setFailed(true)
                        return
                    }
                    setMatching(payload)
                })
                .catch(() => {
                    if (cancelled) return
                    // Erreur (réseau / 4xx / 5xx) : on NE bloque PAS le funnel.
                    setFailed(true)
                })
                .finally(() => {
                    if (!cancelled) setLoading(false)
                })
            return () => {
                cancelled = true
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasValidStateId, hasMatchingPayload])
    )

    const showcase = matching?.showcase || []

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={6}
                    total={7}
                    hideLogo
                    onBack={() => navigation.goBack()}
                />
                <ScrollView
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    {loading ? (
                        <View style={styles.loadingBlock}>
                            <ActivityIndicator color={theme.colors.primary} />
                            <Text style={styles.loadingText}>
                                On cherche les masteurs faits pour {firstname}…
                            </Text>
                        </View>
                    ) : (
                        <SummerTutorsContent
                            firstname={firstname}
                            count={matching?.count}
                            total={matching?.total}
                            tutors={showcase}
                            moreAvatars={matching?.more_avatars}
                            failed={failed}
                        />
                    )}
                    <SummerHelpBanner step={6} />
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Réserver la place"
                        onPress={() =>
                            navigation.navigate("summer/slots-checkout", nextParams)
                        }
                        // Désactivé tant que le matching n'a pas chargé : laisse les
                        // écritures amont (programme) atterrir avant d'ouvrir le
                        // checkout, qui recrée la session de paiement au montage.
                        disabled={loading}
                        color={
                            loading
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: { flex: 1, alignItems: "center", backgroundColor: theme.colors.whiteAlmost },
    frame: { flex: 1, backgroundColor: theme.colors.white, paddingHorizontal: 20 },
    content: { paddingTop: 14, paddingBottom: 24 },
    loadingBlock: { alignItems: "center", paddingVertical: 48, gap: 14 },
    loadingText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 14,
        color: theme.colors.grey,
        textAlign: "center",
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default SummerTutorsScreen
